import { getState } from '../store/project';
import { reviewAndLoad } from '../security/ImportReviewModal';
import type { Project } from '../types';

export interface HtmlExportOpts {
  /** Auto-play on load. */
  autoplay: boolean;
  /** Loop forever (restart at end). */
  loop: boolean;
  /** Show transport / progress / volume controls. */
  controls: boolean;
  /** Click anywhere on the canvas to play/pause. */
  clickToPlay: boolean;
  /** Initial audio volume 0..1 (applies to BGM). */
  volume: number;
  /** Public URL of the editor used for redirect. */
  base?: string;
}

export const DEFAULT_HTML_OPTS: HtmlExportOpts = {
  autoplay: true,
  loop: false,
  controls: true,
  clickToPlay: true,
  volume: 1,
};

function safeName(project: Project): string {
  return (project.name || 'project').replace(/[^\w가-힣\- ]+/g, '_');
}

export function encodeProject(project: Project): string {
  const json = JSON.stringify(project);
  const bytes = new TextEncoder().encode(json);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i] ?? 0);
  return btoa(binary);
}

/** Build a shareable URL that, when opened, reconstitutes the current
    project via ?project=<base64 JSON>. Anyone (or any agent — MCP, GPT)
    can then drop this URL into a tab to open the same project. */
export function buildShareUrl(project: Project, base?: string): string {
  const root = base ?? `${window.location.origin}${window.location.pathname}`;
  const url = new URL(root);
  url.searchParams.set('project', encodeProject(project));
  return url.toString();
}

/** Save the project as a Float:Flash-html5 source file (.float). Same shape
    as the previous JSON; we just renamed the extension so the OS treats it
    as a Flash file rather than a generic JSON. */
export function exportProject(): void {
  const project = getState().project;
  const blob = new Blob([JSON.stringify(project, null, 2)], { type: 'application/json' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${safeName(project)}.float`;
  a.click();
  URL.revokeObjectURL(a.href);
}

/** One-page HTML that opens the project in the live viewer. Tiny — the
    actual rendering still happens at flash.float.do (and only there
    because we need media + fonts + audio players the editor brings).
    Standalone offline rendering is a future export option. */
export function exportHtml(opts: HtmlExportOpts = DEFAULT_HTML_OPTS): void {
  const project = getState().project;
  const base = opts.base ?? 'https://flash.float.do/';
  const url = new URL(base);
  url.searchParams.set('project', encodeProject(project));
  url.searchParams.set('viewer', '1');
  if (opts.autoplay) url.searchParams.set('autoplay', '1');
  if (opts.loop) url.searchParams.set('loop', '1');
  if (!opts.controls) url.searchParams.set('controls', '0');
  if (!opts.clickToPlay) url.searchParams.set('clickToPlay', '0');
  if (opts.volume !== 1) url.searchParams.set('volume', String(opts.volume));

  const targetUrl = url.toString();
  const titleEsc = (project.name || 'Flash').replace(/[<>&]/g, (c) => `&#${c.charCodeAt(0)};`);
  const html = `<!DOCTYPE html>
<html lang="ko">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover">
  <title>${titleEsc} · Float:Flash-html5</title>
  <meta http-equiv="refresh" content="0; url=${targetUrl}">
  <style>
    html, body { margin: 0; height: 100%; background: #0e0e10; color: #e8e8ee;
      font-family: -apple-system, BlinkMacSystemFont, 'Pretendard', sans-serif; }
    body { display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 12px; }
    a { color: #ff5a5f; }
  </style>
</head>
<body>
  <div>로딩 중…</div>
  <a href="${targetUrl}">바로 열기</a>
  <script>location.replace(${JSON.stringify(targetUrl)});</script>
</body>
</html>`;

  const blob = new Blob([html], { type: 'text/html' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${safeName(project)}.html`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export function importProject(): void {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.float,.flt,.json,application/json';
  input.addEventListener('change', () => {
    const file = input.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const obj = JSON.parse(String(reader.result)) as Project;
        if (!obj || obj.version !== 1 || !Array.isArray(obj.layers)) {
          alert('잘못된 프로젝트 파일입니다');
          return;
        }
        // Always go through the security review modal — even files we wrote
        // ourselves may have been edited by hand or shared.
        void reviewAndLoad(obj);
      } catch (err) {
        alert('JSON 파싱 실패');
        console.error(err);
      }
    };
    reader.readAsText(file);
  });
  input.click();
}
